// The Help button in an app's header and the first-visit nudge (18-help-and-training.md §18.1).
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { Button } from './Button';

export interface HelpMenuLabels {
  help: string;
  guided: string;
  demo: string;
  training: string;
}

export const HELP_MENU_LABELS_EN: HelpMenuLabels = {
  help: 'Help',
  guided: 'Walk me through this page',
  demo: 'Watch a demo',
  training: 'My training',
};

export interface HelpMenuProps {
  /** The guided walkthrough of this page. Leave out when the page has none: the item is hidden. */
  onGuided?: (() => void) | undefined;
  /** The demo of this page. Leave out when the page has none. */
  onDemo?: (() => void) | undefined;
  /** Opens My training. Leave out where there is no such page (the partner link). */
  onTraining?: (() => void) | undefined;
  labels?: Partial<HelpMenuLabels> | undefined;
  /** The Help button, so a walkthrough can give focus back to it when it ends. */
  buttonRef?: RefObject<HTMLButtonElement | null> | undefined;
}

interface Item {
  label: string;
  run: () => void;
}

export function HelpMenu({ onGuided, onDemo, onTraining, labels, buttonRef }: HelpMenuProps) {
  const t = { ...HELP_MENU_LABELS_EN, ...labels };
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const ownButton = useRef<HTMLButtonElement | null>(null);
  const button = buttonRef ?? ownButton;
  const panel = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const items: Item[] = [
    ...(onGuided ? [{ label: t.guided, run: onGuided }] : []),
    ...(onDemo ? [{ label: t.demo, run: onDemo }] : []),
    ...(onTraining ? [{ label: t.training, run: onTraining }] : []),
  ];

  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const nodes = [...(panel.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
      if (nodes.length === 0) return;
      e.preventDefault();
      const at = nodes.indexOf(document.activeElement as HTMLElement);
      const next = e.key === 'ArrowDown' ? at + 1 : at - 1;
      nodes[(next + nodes.length) % nodes.length]?.focus();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, button]);

  if (items.length === 0) return null;
  return (
    <div className="eb-help" ref={root}>
      <button
        ref={button}
        type="button"
        className="eb-help__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="eb-help__mark" aria-hidden="true">
          ?
        </span>
        <span className="eb-help__label">{t.help}</span>
      </button>
      {open ? (
        <div className="eb-help__panel" id={menuId} role="menu" aria-label={t.help} ref={panel}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className="eb-menuitem"
              onClick={() => {
                setOpen(false);
                item.run();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export interface WalkthroughNudgeLabels {
  /** "New to this page? Take the 1-minute walkthrough." */
  text: (minutes: number) => string;
  start: string;
  notNow: string;
}

export const NUDGE_LABELS_EN: WalkthroughNudgeLabels = {
  text: (minutes) => `New to this page? Take the ${minutes}-minute walkthrough.`,
  start: 'Start',
  notNow: 'Not now',
};

export interface WalkthroughNudgeProps {
  /** Roughly how long it takes (`walkthroughMinutes`). */
  minutes: number;
  onStart: () => void;
  onDismiss: () => void;
  labels?: Partial<WalkthroughNudgeLabels> | undefined;
}

/** A small card offering the page's walkthrough. It never blocks the page. */
export function WalkthroughNudge({ minutes, onStart, onDismiss, labels }: WalkthroughNudgeProps) {
  const t = { ...NUDGE_LABELS_EN, ...labels };
  const text = t.text(Math.max(1, Math.round(minutes)));
  return (
    <aside className="eb-nudge" aria-label={text} role="status">
      <p className="eb-nudge__text">{text}</p>
      <div className="eb-nudge__actions">
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          {t.notNow}
        </Button>
        <Button variant="primary" size="sm" onClick={onStart}>
          {t.start}
        </Button>
      </div>
    </aside>
  );
}
